/**
 * Budget Line Request service (D-Core multi-period — ticket t101354)
 *
 * NOTE: FE based on API contract DRAFT (docs/api/t101354-budget-line-requests.md).
 * Must re-verify với BE thực sau khi BE deliver xong (Rule 4).
 */

import ApiService from '@/core/services/api.service';

const BASE = 'v2/budget-line-requests';

/**
 * Transform 1 raw API response (single request) → flat shape phù hợp UI.
 * - Đảm bảo `periods` luôn là array {month, year, amount}
 * - Đảm bảo `totalAmount` luôn tính được (BE đã trả, FE fallback nếu trống)
 * - Đảm bảo `blockedPeriods` luôn là array
 * (Rule 8 — never dump raw response into Vuex)
 */
export function transformRequest(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const periods = Array.isArray(raw.periods)
    ? raw.periods.map((p) => {
        const rawCb = p && p.currentBudget;
        const currentBudget = rawCb && typeof rawCb === 'object'
          ? {
              budgetLineId: rawCb.budgetLineId != null ? Number(rawCb.budgetLineId) : null,
              // t101354 — BudgetLine năm (parent), dùng cho link "Xem chi tiết ngân sách năm"
              yearBudgetLineId: rawCb.yearBudgetLineId != null ? Number(rawCb.yearBudgetLineId) : null,
              allocatedAmount: Number(rawCb.allocatedAmount) || 0,
              usedAmount: Number(rawCb.usedAmount) || 0,
              remainingAmount: Number(rawCb.remainingAmount) || 0,
              status: rawCb.status != null ? Number(rawCb.status) : null,
            }
          : null;
        return {
          id: p.id != null ? Number(p.id) : null,
          month: Number(p.month),
          year: Number(p.year),
          amount: Number(p.amount) || 0,
          currentBudget,
        };
      })
    : [];

  const totalAmount = Number(raw.totalAmount);
  const computedTotal = periods.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  return {
    id: raw.id != null ? Number(raw.id) : null,
    code: raw.code || '',
    companyId: raw.companyId != null ? Number(raw.companyId) : null,
    companyName: raw.companyName || '',
    departmentId: raw.departmentId != null ? Number(raw.departmentId) : null,
    departmentName: raw.departmentName || '',
    storeId: raw.storeId != null ? Number(raw.storeId) : null,
    storeName: raw.storeName || '',
    costItemId: raw.costItemId != null ? Number(raw.costItemId) : null,
    costItemName: raw.costItemName || '',
    reason: raw.reason || '',
    status: raw.status,
    periods,
    totalAmount: Number.isFinite(totalAmount) && totalAmount > 0 ? totalAmount : computedTotal,
    approvers: Array.isArray(raw.approvers) ? raw.approvers : [],
    canEdit: !!raw.canEdit,
    canApprove: !!raw.canApprove,
    canUndo: !!raw.canUndo,
    blockedPeriods: Array.isArray(raw.blockedPeriods) ? raw.blockedPeriods : [],
    budgetLineId: raw.budgetLineId || null,
    history: Array.isArray(raw.history) ? raw.history : [],
    createdBy: raw.createdBy || null,
    createdByName: raw.createdByName || '',
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  };
}

/** Build period payload {month, year, amount} từ form state */
export function buildPeriodPayload(periods) {
  return (periods || [])
    .filter((p) => p && p.month && p.year)
    .map((p) => ({
      month: Number(p.month),
      year: Number(p.year),
      amount: Number(p.amount) || 0,
    }));
}

export default {
  async list(params = {}) {
    const { data } = await ApiService.query(BASE, { params });
    const raw = (data && data.data) || {};
    // BE list trả dataset (legacy) hoặc data array — handle cả hai
    const dataset = raw.dataset || raw || [];
    const list = Array.isArray(dataset) ? dataset : [];
    return {
      list: list.map(transformRequest).filter(Boolean),
      totalRow: raw.totalRow || raw.total || list.length,
      totalPage: raw.totalPage || 1,
      meta: raw.meta || null,
    };
  },

  async getById(id) {
    const { data } = await ApiService.get(`${BASE}/${id}`);
    return transformRequest((data && data.data) || {});
  },

  async create(payload) {
    const body = {
      companyId: payload.companyId,
      departmentId: payload.departmentId,
      storeId: payload.storeId || null,
      costItemId: payload.costItemId,
      reason: payload.reason,
      approvers: payload.approvers || [],
      periods: buildPeriodPayload(payload.periods),
    };
    const { data } = await ApiService.post(BASE, body);
    return {
      message: (data && data.message) || '',
      data: transformRequest((data && data.data) || {}),
    };
  },

  async update(id, payload) {
    const body = {
      companyId: payload.companyId,
      departmentId: payload.departmentId,
      storeId: payload.storeId || null,
      costItemId: payload.costItemId,
      reason: payload.reason,
      approvers: payload.approvers || [],
      periods: buildPeriodPayload(payload.periods),
    };
    const { data } = await ApiService.put(`${BASE}/${id}`, body);
    return {
      message: (data && data.message) || '',
      data: transformRequest((data && data.data) || {}),
    };
  },

  async submit(id) {
    const { data } = await ApiService.post(`${BASE}/${id}/submit`, {});
    return {
      message: (data && data.message) || '',
      data: transformRequest((data && data.data) || {}),
    };
  },

  async changeStatus(id, body) {
    const { data } = await ApiService.patch(`${BASE}/${id}/change-status`, body);
    return {
      message: (data && data.message) || '',
      data: transformRequest((data && data.data) || {}),
    };
  },

  async remove(id) {
    const { data } = await ApiService.delete(`${BASE}/${id}`);
    return { message: (data && data.message) || '' };
  },

  /**
   * Endpoint 8 — GET /locked-periods?companyId&departmentId&costItemId&storeId
   * Trả về [{month, year}] các BudgetLine LOCKED khớp tenant scope để PeriodPicker disable.
   * Yêu cầu: phải có đủ companyId+departmentId+costItemId (storeId optional).
   */
  async getLockedPeriods({ companyId, departmentId, costItemId, storeId } = {}) {
    if (!companyId || !departmentId || !costItemId) return [];
    const params = {
      companyId,
      departmentId,
      costItemId,
    };
    if (storeId) params.storeId = storeId;
    const { data } = await ApiService.query(`${BASE}/locked-periods`, { params });
    const rows = (data && data.data) || [];
    return Array.isArray(rows)
      ? rows.map((p) => ({ month: Number(p.month), year: Number(p.year) }))
      : [];
  },

  /**
   * Endpoint 7 — POST /check-duplicate (status: 'ok' | 'warning' | 'blocked')
   * payload: { companyId, departmentId, storeId, costItemId, periods:[{month,year}], excludeRequestId? }
   */
  async checkDuplicate(payload) {
    const body = {
      companyId: payload.companyId,
      departmentId: payload.departmentId,
      storeId: payload.storeId || null,
      costItemId: payload.costItemId,
      periods: (payload.periods || []).map((p) => ({
        month: Number(p.month),
        year: Number(p.year),
      })),
      excludeRequestId: payload.excludeRequestId || undefined,
    };
    const { data } = await ApiService.post(`${BASE}/check-duplicate`, body);
    const result = (data && data.data) || {};
    return {
      status: result.status || 'ok',
      conflictingRequests: Array.isArray(result.conflictingRequests)
        ? result.conflictingRequests
        : [],
    };
  },

  /**
   * Helper: chạy check-duplicate trước rồi submit/update.
   *
   * @param {Object} opts
   * @param {Object} opts.payload — payload đầy đủ để check-duplicate + submit
   * @param {Function} opts.confirmWarningFn — async () => boolean, gọi khi warning để user confirm
   * @param {Function} opts.submitFn — async () => result, hành động chính (vd: create -> submit, hoặc update)
   * @returns {Promise<{status, result}>}
   *   - status 'blocked' → throw, không gọi submitFn
   *   - status 'warning' → confirmWarningFn(false → throw 'CANCELLED'; true → call submitFn)
   *   - status 'ok' → call submitFn ngay
   */
  async submitWithDuplicateCheck({ payload, confirmWarningFn, submitFn }) {
    const check = await this.checkDuplicate(payload);
    if (check.status === 'blocked') {
      const err = new Error('DUPLICATE_PENDING_REQUEST');
      err.code = 'DUPLICATE_PENDING_REQUEST';
      err.details = { conflictingRequests: check.conflictingRequests };
      throw err;
    }
    if (check.status === 'warning') {
      const ok = await (confirmWarningFn ? confirmWarningFn(check.conflictingRequests) : Promise.resolve(true));
      if (!ok) {
        const err = new Error('CANCELLED_BY_USER');
        err.code = 'CANCELLED_BY_USER';
        throw err;
      }
    }
    const result = await submitFn();
    return { status: check.status, result };
  },
};
